import React, { useRef } from 'react';
import {
  useDateField,
  DateValue,
  AriaDatePickerProps,
} from '@react-aria/datepicker';
import { DateSegment } from './DateSegment';
import { useLocale, useMessageFormatter } from '@react-aria/i18n';
import {
  useDateFieldState,
  DateRangePickerState,
} from '@react-stately/datepicker';
import { createCalendar } from '@internationalized/date';
import styles from '@danske/sapphire-css/components/dateField/dateField.module.css';
import { CloseOutline } from '@danske/sapphire-icons/react';
import { DateFieldButton } from './DateFieldButton';
import { segmentTypes, isAnyModified } from './utils/segments';
import intlMessages from '../i18n';
import { useCustomDateFormatLocale } from '../../SapphireProvider';
import { Icon } from '../../Icon';

export interface DateRangeInputProps<T extends DateValue> {
  dateRangePickerState: DateRangePickerState;
  startFieldProps: AriaDatePickerProps<T>;
  endFieldProps: AriaDatePickerProps<T>;
  /**
   * We pass the trigger button id to the clear button as well.
   * This is a hacky way to get around the fact the the internal focus manager
   * of date fields focuses the clear button when last year is typed in.
   *
   * Source of issue is here:
   * https://github.com/adobe/react-spectrum/blob/10a43de887ffc28913c770a33573aebf3df786fc/packages/%40react-aria/datepicker/src/useDateRangePicker.ts#L90
   * Discussion here:
   * https://github.com/adobe/react-spectrum/discussions/4677
   *
   * The bug even appears on react-aria examples:
   * https://react-spectrum.adobe.com/react-aria/DateRangePicker.html#state
   *
   * It's not nice that we have 2 elements with the same id in the DOM but:
   *  - both elements are on the screen in a specific state of the date range
   *  field, not always
   *  - and it's not an issue for accessibility and screenreaders because they
   *  simply pick the first id when reading "labelledby" or other references to
   *  an id, and the first id is always the calendar trigger button.
   *
   * Alternative solution was to set a tabindex=-1 on the clear button and
   * manually control the focus state for accessibility, but that seemed too
   * complex and error prone.
   **/
  triggerButtonId?: string;
  noClearButton?: boolean;
  size?: 'lg' | 'md';
}

/**
 * @internal
 */
export function DateRangeInput<T extends DateValue>({
  dateRangePickerState,
  startFieldProps,
  endFieldProps,
  triggerButtonId,
  noClearButton = false,
  size = 'lg',
}: DateRangeInputProps<T>): React.JSX.Element {
  const startFieldRef = useRef(null);
  const endFieldRef = useRef(null);
  const formatMessage = useMessageFormatter(intlMessages);

  const { locale } = useLocale();
  const customDateFormatLocale = useCustomDateFormatLocale();

  const startFieldState = useDateFieldState({
    ...startFieldProps,
    locale: customDateFormatLocale || locale,
    createCalendar,
  });

  const endFieldState = useDateFieldState({
    ...endFieldProps,
    locale: customDateFormatLocale || locale,
    createCalendar,
  });

  const { fieldProps: startFieldAriaProps } = useDateField(
    startFieldProps,
    startFieldState,
    startFieldRef
  );
  const { fieldProps: endFieldAriaProps } = useDateField(
    endFieldProps,
    endFieldState,
    endFieldRef
  );

  const clearFields = () => {
    segmentTypes.forEach((segmentType) => {
      startFieldState.clearSegment(segmentType);
      endFieldState.clearSegment(segmentType);
    });

    /**
     * Workaround for clearing
     *
     * PROBLEM
     * - UI: Setting value in both startField and endField
     * - Code: Sets a placeholder value i useDateRangePickerState ({ start: date, end: date })
     * - UI: Clicking clear button
     * - Code: startFieldState.clearSegment() runs and sets start-prop of placeholder to null
     * - Code: endFieldState.clearSegment() runs and sets end-prop of placeholder to null,
     *   BUT THE value INSIDE dateRangePickerState.setDateTime() IS STALE AND STILL HAS DATE VALUES FOR BOTH start AND end
     * - Code: so a placholder value is set to ({ start: date, end: null })
     *
     * POTENTIAL SOLUTION
     * If the value in useDateRangePickerState was saved as a ref
     * (like here: https://github.com/adobe/react-spectrum/pull/2510/commits/ca696af45e1f4548e1ace06e5de6d4f297f46eeb?diff=split&w=0#diff-eb983dc43959711e7dea6f33bcbbe97aa0bab850f24441753cdbd885125f3b71R50-R51)
     * and valueRef.current would be the value used in setDateTime of useDateRangePickerState,
     * both dates would be correctly cleared as the latest start value (which is null) would be known by setDateTime run by endFieldState.clearSegment()
     * And we still want to use clearSegment as that takes care of remembering last selected value for reselection.
     * It's suboptimal to have to call that continuously though, so a clearAllSegments function could be ideal.
     *
     * Without the change in Adobe's hooks, we have to ourselves dateRangePickerState.setValue to null.
     * But we'll be keeping the *FieldState.clearSegment calls as those will make sure to save new date to start from on next selection.
     * Ie if 2026 was selected, that's where a reselection with arrow keys will start from.
     * Only seems to take effect for the later of the calls though – currently endFieldState.
     *
     * Submitted a discussion to Adobe about this: https://github.com/adobe/react-spectrum/issues/4986
     */
    // Clear the individual field states
    startFieldState.setValue(null!);
    endFieldState.setValue(null!);

    // Clear the parent range state
    dateRangePickerState.setValue({ start: null!, end: null! }); // null works on runtime
  };

  return (
    <>
      <div className={styles['sapphire-date-field__input-group']}>
        <div
          {...startFieldAriaProps}
          className={styles['sapphire-date-field__input']}
          ref={startFieldRef}
        >
          {startFieldState.segments.map((segment, i) => (
            <DateSegment key={i} segment={segment} state={startFieldState} />
          ))}
        </div>
        <span className={styles['sapphire-date-field__range-separator']}></span>
        <div
          {...endFieldAriaProps}
          className={styles['sapphire-date-field__input']}
          ref={endFieldRef}
        >
          {endFieldState.segments.map((segment, i) => (
            <DateSegment key={i} segment={segment} state={endFieldState} />
          ))}
        </div>
      </div>
      {!noClearButton &&
        !startFieldProps.isReadOnly &&
        !endFieldProps.isReadOnly &&
        !startFieldProps.isDisabled &&
        !endFieldProps.isDisabled &&
        isAnyModified([
          ...startFieldState.segments,
          ...endFieldState.segments,
        ]) && (
          <DateFieldButton
            id={triggerButtonId}
            aria-label={formatMessage('clear')}
            onPress={clearFields}
            isClearButton
          >
            <Icon size={size === 'md' ? 'sm' : 'md'}>
              <CloseOutline />
            </Icon>
          </DateFieldButton>
        )}
    </>
  );
}
